import { useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useEmpresaAtiva } from "../hooks/useEmpresaAtiva";
import { useVerComo, type VerComoAlvo } from "../hooks/useVerComo";
import { apiFetch } from "../lib/api";
import { cn } from "../lib/utils";
import { Eye, Search, Users, UserCog, Check, Building2 } from "lucide-react";
import { CamadaDeDialogo, FecharDialogo } from "./pele/CamadaDeDialogo"; import { EsqueletoLista } from "./pele/Esqueleto"; /* direto do arquivo, não do barril: o barril puxava a pele inteira pro caminho crítico */

/* ────────────────────────────────────────────────────────────────────────
 * "Ver como" — controle na sidebar (logo abaixo da logo).
 * Segmenta entre OPERADOR (visão real do admin) e CLIENTE (assume a visão de
 * um usuário escolhido). Em CLIENTE, abre o seletor de clientes + usuários
 * internos. Some pra quem não é admin. Mantém as telas limpas (um lugar só).
 * ──────────────────────────────────────────────────────────────────────── */

interface UsuarioApi {
  id: string;
  nome: string | null;
  email: string | null;
  role: string | null;
  ativo: boolean | null;
  cliente_id: string | null;
  clientes?: { nome: string } | null;
  empresas?: Array<{ id: string; nome: string; slug: string; role_empresa: string; perfil: string }>;
}

type Candidato = Omit<VerComoAlvo, "empresaSlug">;

function papelLabel(role: string): string {
  if (role === "cliente") return "Cliente";
  if (role === "encarregado") return "Encarregado";
  if (role === "admin") return "Admin";
  return role;
}

export function VerComoControl({ mostrarLabels, accentColor }: { mostrarLabels: boolean; accentColor: string }) {
  const { user, getAccessToken } = useAuth();
  const { ativa } = useEmpresaAtiva();
  const { alvo, setAlvo, limpar } = useVerComo();
  const navigate = useNavigate();
  const [aberto, setAberto] = useState(false);

  // Só admin enxerga a lente (é ferramenta de operação/demonstração).
  if (user?.role !== "admin") return null;
  const slug = ativa?.slug ?? "";
  const emRegula = slug === "regula360" || slug === "vitrine-reg";

  function escolher(c: Candidato) {
    setAlvo({ ...c, empresaSlug: slug });
    setAberto(false);
    // Leva pra "casa" da pessoa: portal do cliente (Regula/facilities) ou painel.
    if (c.role === "cliente") navigate(emRegula ? "/portal-regula" : "/portal");
    else navigate("/");
  }

  // Modo recolhido (só ícones): um botão de olho que abre o seletor.
  if (!mostrarLabels) {
    return (
      <>
        <div className="px-2 py-2 border-b border-white/10 flex justify-center">
          <button
            onClick={() => { if (alvo) { limpar(); navigate("/"); } else setAberto(true); }}
            title={alvo ? `Vendo como ${alvo.nome}: clique pra voltar ao Operador` : "Ver como cliente"}
            className="p-2 rounded-md hover:bg-white/10 transition-colors"
            style={{ color: alvo ? accentColor : "rgba(255,255,255,0.6)" }}
          >
            <Eye className="h-4 w-4" />
          </button>
        </div>
        {aberto && <SeletorModal slug={slug} meuId={user?.id} token={getAccessToken()} onEscolher={escolher} onFechar={() => setAberto(false)} />}
      </>
    );
  }

  return (
    <div className="px-4 py-3 border-b border-white/10">
      <div className="text-[10px] tracking-[0.18em] uppercase font-semibold mb-1.5" style={{ color: accentColor, opacity: 0.7 }}>
        Modo de visão
      </div>
      <div className="flex rounded-lg p-0.5 bg-white/5 text-xs font-medium">
        <button
          onClick={() => { const tinhaLente = !!alvo; limpar(); if (tinhaLente) navigate("/"); }}
          className={cn("flex-1 py-1.5 rounded-md transition-colors", !alvo ? "bg-white/15 text-white" : "text-white/55 hover:text-white")}
        >
          Operador
        </button>
        <button
          onClick={() => setAberto(true)}
          className={cn("flex-1 py-1.5 rounded-md transition-colors", alvo ? "text-white" : "text-white/55 hover:text-white")}
          style={alvo ? { backgroundColor: accentColor, color: "#161410" } : undefined}
        >
          Cliente
        </button>
      </div>
      {alvo && (
        <div className="mt-2 flex items-center gap-2 text-[11px] text-white/70">
          <Eye className="h-3 w-3 shrink-0" style={{ color: accentColor }} />
          <span className="truncate">
            Vendo como <strong className="text-white">{alvo.nome}</strong> · {papelLabel(alvo.role)}
          </span>
        </div>
      )}
      {aberto && <SeletorModal slug={slug} meuId={user?.id} token={getAccessToken()} onEscolher={escolher} onFechar={() => setAberto(false)} />}
    </div>
  );
}

function SeletorModal({
  slug,
  meuId,
  token,
  onEscolher,
  onFechar,
}: {
  slug: string;
  meuId?: string;
  token: string | null;
  onEscolher: (c: Candidato) => void;
  onFechar: () => void;
}) {
  const [busca, setBusca] = useState("");
  const buscaRef = useRef<HTMLInputElement>(null);
  const { data, isLoading } = useQuery({
    queryKey: ["ver-como-usuarios"],
    queryFn: () => apiFetch<{ data: UsuarioApi[] }>("/usuarios", token),
    staleTime: 60 * 1000,
  });

  const candidatos = useMemo<Candidato[]>(() => {
    const lista = data?.data ?? [];
    return lista
      .filter((u) => u.ativo !== false && u.id !== meuId)
      .map((u): Candidato | null => {
        const emp = (u.empresas ?? []).find((e) => e.slug === slug);
        if (!emp) return null; // só usuários DESTA empresa
        // PAPEL = role global (admin | encarregado | cliente) — é o que o menu
        // (NAV.roles) e o roteamento usam. role_empresa ("colaborador" etc.) é
        // outro vocabulário e deixaria o menu vazio. O perfil (RBAC) vem do
        // vínculo da empresa.
        return {
          id: u.id,
          nome: u.nome || u.email || "Usuário",
          email: u.email,
          role: u.role || "admin",
          perfil: emp.perfil || "admin",
          clienteId: u.cliente_id,
          clienteNome: u.clientes?.nome ?? null,
        };
      })
      .filter((c): c is Candidato => c !== null);
  }, [data, slug, meuId]);

  const filtrados = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return candidatos;
    return candidatos.filter(
      (c) => c.nome.toLowerCase().includes(q) || (c.email ?? "").toLowerCase().includes(q) || (c.clienteNome ?? "").toLowerCase().includes(q)
    );
  }, [candidatos, busca]);

  const clientes = filtrados.filter((c) => c.role === "cliente");
  const equipe = filtrados.filter((c) => c.role !== "cliente");

  return (
    <CamadaDeDialogo
      onClose={onFechar}
      title="Ver como…"
      className="z-[60] flex items-start justify-center p-4 sm:pt-24 bg-black/50"
      painelClassName="w-full max-w-md rounded-xl bg-card border shadow-2xl overflow-hidden"
      // O `autoFocus` do campo de busca é o ponto desta tela: abre e já digita.
      // Sem isto, a mecânica pousaria o foco no primeiro focável — o X.
      focoInicialRef={buscaRef}
    >
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="flex items-center gap-2">
            <Eye className="h-4 w-4 text-primary" />
            <h2 className="text-sm font-semibold">Ver como…</h2>
          </div>
          <FecharDialogo onClose={onFechar} />
        </div>
        <div className="px-4 py-2.5 border-b">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              ref={buscaRef}
              autoFocus
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar por nome, e-mail ou cliente…"
              className="w-full h-9 pl-8 pr-3 rounded-md border bg-background text-sm"
            />
          </div>
        </div>
        <div className="max-h-[55vh] overflow-y-auto py-1">
          {isLoading && <EsqueletoLista className="px-4 py-3" linhas={4} alturaLinha="h-10" rotulo="Carregando as pessoas…" />}
          {!isLoading && filtrados.length === 0 && (
            <div className="px-4 py-6 text-sm text-muted-foreground text-center">Nenhum usuário encontrado nesta empresa.</div>
          )}
          {clientes.length > 0 && <Secao titulo="Clientes do portal" icon={Building2} />}
          {clientes.map((c) => (
            <Linha key={c.id} c={c} onClick={() => onEscolher(c)} />
          ))}
          {/* A UI MENTIA (red team 09/07): oferecia "Equipe interna" como alvo,
              mas o backend só honra ?como= de role=cliente (portalAcesso:46) —
              escolher um encarregado dava tela vazia (fail-closed silencioso)
              ou os dados do próprio admin. Agora é explícito: não suportado. */}
          {equipe.length > 0 && (
            <>
              <Secao titulo="Equipe interna" icon={UserCog} />
              <div className="px-4 py-2 text-[11.5px] text-muted-foreground">
                Ver como membro da equipe ainda não é suportado, a lente só reproduz fielmente a visão de um
                <b> cliente do portal</b>. ({equipe.length} pessoa{equipe.length > 1 ? "s" : ""} na equipe desta empresa.)
              </div>
            </>
          )}
        </div>
    </CamadaDeDialogo>
  );
}

function Secao({ titulo, icon: Icon }: { titulo: string; icon: typeof Users }) {
  return (
    <div className="flex items-center gap-1.5 px-4 pt-3 pb-1 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
      <Icon className="h-3 w-3" />
      {titulo}
    </div>
  );
}

function Linha({ c, onClick }: { c: Candidato; onClick: () => void }) {
  return (
    <button onClick={onClick} className="w-full flex items-center gap-3 px-4 py-2 hover:bg-muted/60 text-left transition-colors group">
      <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center text-xs font-semibold shrink-0">
        {c.nome.slice(0, 1).toUpperCase()}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium truncate">{c.nome}</div>
        <div className="text-[11px] text-muted-foreground truncate">
          {papelLabel(c.role)}
          {c.clienteNome ? ` · ${c.clienteNome}` : ""}
          {c.perfil && c.perfil !== "admin" ? ` · ${c.perfil}` : ""}
        </div>
      </div>
      <Check className="h-4 w-4 text-primary opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
    </button>
  );
}
